<script setup lang="ts">
import { computed } from 'vue'

import { useWorkspaceStore } from '@/stores/workspaceStore'
import type { Tour } from '@/types/tour'

import TourDetailView from './TourDetailPanel.vue'
import TrackList from './TrackList.vue'
import WaypointList from './WaypointList.vue'

const workspaceStore = useWorkspaceStore()

const props = withDefaults(
  defineProps<{
    detachedTour?: Tour | null
    selectedWaypointIndex?: number | null
    waypointOpenSource?: 'list' | 'map' | null
  }>(),
  {
    detachedTour: null,
    selectedWaypointIndex: null,
    waypointOpenSource: null,
  },
)

const emit = defineEmits<{
  'zoom-tour': [tourId: string]
  'zoom-track': [tourId: string, trackIndex: number]
  'zoom-waypoint': [tourId: string, waypointIndex: number]
  'zoom-route-point': [tourId: string, routePointIndex: number]
  'open-waypoint-detail': [tourId: string, waypointIndex: number, source?: 'list' | 'map']
  'close-waypoint-detail': []
  'delete-waypoint': [tourId: string, waypointIndex: number]
  'edit-tour': [tourId: string]
  'enter-track-edit': [tourId: string, trackClientId: string]
  'retain-unpinned-tour-detail': [tour: Tour]
  back: []
}>()

// Get the currently selected tour
const selectedTour = computed(() => {
  return workspaceStore.selectedTour ?? props.detachedTour
})

// Handler functions
const handleZoomTrack = (tourId: string, trackIndex: number) => {
  emit('zoom-track', tourId, trackIndex)
}

const handleZoomWaypoint = (tourId: string, waypointIndex: number) => {
  emit('zoom-waypoint', tourId, waypointIndex)
}

const handleOpenWaypointDetail = (
  tourId: string,
  waypointIndex: number,
  source: 'list' | 'map' = 'list',
) => {
  emit('open-waypoint-detail', tourId, waypointIndex, source)
}

const handleCloseWaypointDetail = () => {
  emit('close-waypoint-detail')
}

const handleDeleteWaypoint = (tourId: string, waypointIndex: number) => {
  emit('delete-waypoint', tourId, waypointIndex)
}

const handleZoomRoutePoint = (tourId: string, routePointIndex: number) => {
  emit('zoom-route-point', tourId, routePointIndex)
}

const handleEditTour = (tourId: string) => {
  emit('edit-tour', tourId)
}

const handleBackToList = () => {
  emit('back')
}

const handleEnterTrackEdit = (tourId: string, trackClientId: string) => {
  emit('enter-track-edit', tourId, trackClientId)
}

const handleZoomTour = (tourId: string) => {
  emit('zoom-tour', tourId)
}
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col bg-white overflow-hidden">
    <!-- If a tour is selected, show the tour detail view -->
    <TourDetailView
      v-if="selectedTour"
      :tour="selectedTour"
      :selected-waypoint-index="props.selectedWaypointIndex"
      :waypoint-open-source="props.waypointOpenSource"
      @back="handleBackToList"
      @edit-tour="handleEditTour"
      @zoom-tour="handleZoomTour"
      @zoom-track="handleZoomTrack"
      @zoom-waypoint="handleZoomWaypoint"
      @zoom-route-point="handleZoomRoutePoint"
      @open-waypoint-detail="handleOpenWaypointDetail"
      @close-waypoint-detail="handleCloseWaypointDetail"
      @delete-waypoint="handleDeleteWaypoint"
      @enter-track-edit="handleEnterTrackEdit"
      @retain-unpinned-tour-detail="emit('retain-unpinned-tour-detail', $event)"
    />

    <!-- Otherwise, show a  s and waypoints list -->
    <div v-else class="flex min-h-0 flex-1 flex-col">
      <TrackList @zoom-track="handleZoomTrack" class="flex-1" />

      <WaypointList
        @zoom-waypoint="handleZoomWaypoint"
        @open-waypoint-detail="handleOpenWaypointDetail"
        class="flex-1"
      />
    </div>
  </div>
</template>
